import { timer } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Component, computed, HostListener, inject } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

import { ButtonComponent, InputTextDropdownComponent } from "src/app/shared";
import { AuthService, customValidations, LookupsService, fallbackOnError } from "src/app/core";

@Component({
  selector: "app-verify",
  standalone: true,
  imports: [RouterLink, TranslateModule, ReactiveFormsModule, ButtonComponent, InputTextDropdownComponent],
  templateUrl: "./verify.component.html",
  styleUrl: "./verify.component.scss",
})
export class VerifyComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private lookupsService = inject(LookupsService);

  otpForm!: FormGroup;
  allowResend = true;
  otpValue = "";
  counter = "";
  countries = toSignal(this.lookupsService.getCountries().pipe(fallbackOnError([])), { initialValue: [] });

  currentPhoneCountryCode = computed(() => {
    return "+968";

    // const countryCode = window.navigator.language.split("-").pop();
    // const country = this.countries().find((c) => c.code === countryCode);

    // if (!country) return null;
    // return country.phoneCode as string;
  });

  private resendTimes = 0;
  private resendMethods: Record<number, number[]> = {
    1: [1, 0],
    2: [3, 0],
    3: [5, 0],
  };

  get token() {
    return this.activatedRoute.snapshot.queryParamMap.get("token") || "";
  }

  get userPhoneNumber() {
    return localStorage.getItem("phone");
  }

  get otp() {
    return this.otpForm?.get("otp");
  }

  get phoneCountryCode() {
    if (!this.countries().length && !this.otpForm.get("countryCode")?.value) return null;

    const country = this.countries().find((c) => c.phoneCode === this.otpForm.get("countryCode")?.value);

    if (!country) return null;

    return country.code;
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.

    if (this.token) {
      this.initVerifyEmailForm();
      this.verifyOtp();
    } else this.initVerifyOTPForm();

    this.countDown(0, 30);
  }

  initVerifyOTPForm() {
    this.otpForm = this.fb.group({
      otp: [null, Validators.required],
      phone: [localStorage.getItem("phone"), Validators.required],
      countryCode: [null, Validators.required],
    });
  }

  initVerifyEmailForm() {
    this.otpForm = this.fb.group({
      confirmationToken: [this.token],
    });
  }

  verifyOtp() {
    this.authService.confirmOtp(this.otpForm.value).subscribe({
      next: () => this.router.navigateByUrl("/auth/login"),
      error: () => this.resetOTPInputValue(),
    });
  }

  getPhoneCountryCode(countryCode: string | number | boolean) {
    const country = this.countries().find((c) => c.phoneCode === countryCode);

    if (country) {
      const countryPhoneRegex = (country as any).phoneCodeRegex;

      this.otpForm.get("phone")?.setValidators([Validators.required, customValidations.withCustomMobileNumberRegex(countryPhoneRegex)]);
    }
  }

  resendOTP() {
    const username = localStorage.getItem("username");

    let body: Record<string, unknown> = {};

    if (username?.includes("@")) {
      body = {
        email: username,
      };
    } else {
      if (!this.otpForm.get("phone")?.value) {
        this.otpForm.get("phone")?.markAsTouched();
        return;
      }

      body = {
        phone: this.userPhoneNumber || this.otpForm.get("phone")?.value,
      };
    }

    this.resendTimes += 1;
    this.countDown(this.resendMethods[this.resendTimes][0], this.resendMethods[this.resendTimes][1]);
    this.authService.resendMobileOtp(body).subscribe();
  }

  onKeyUpEvent(index: number, event: KeyboardEvent) {
    const eventCode = +event.code;

    if (this.getCodeBoxElement(index)["value"].length === 1) {
      if (index !== 4) {
        this.getCodeBoxElement(index + 1)?.focus();
      } else {
        this.getCodeBoxElement(index)?.blur();

        for (let item = 1; item <= index; item++) {
          const currentElement = this.getCodeBoxElement(item);

          if (currentElement && currentElement["value"]) {
            this.otpValue = this.otpValue + currentElement["value"];
          }
        }

        this.otpForm.get("otp")?.setValue(this.otpValue);
      }
    } else {
      this.otpValue = "";
      this.otpForm.get("otp")?.setValue(this.otpValue);
    }

    if (eventCode === 8 && index !== 1) {
      this.getCodeBoxElement(index - 1).focus();
    }
  }

  onKeyDownEvent(event: KeyboardEvent) {
    const eventCode = +event.code;

    if (eventCode !== 8) {
      if ((event.target as HTMLInputElement)?.value) {
        (event.target as HTMLInputElement).value = "";
      }
    }
  }

  onFocusEvent(index: number) {
    for (let item = 1; item < index; item++) {
      const currentElement = this.getCodeBoxElement(item);

      if (!currentElement["value"]) {
        currentElement.focus();
        break;
      }
    }
  }

  private getCodeBoxElement(index: number) {
    return (document.getElementById("codeBox" + index) as HTMLInputElement) || null;
  }

  private resetOTPInputValue() {
    this.otpForm.get("otp")?.reset();

    for (let i = 0; i < 4; i++) {
      const currentElement = this.getCodeBoxElement(i + 1);
      currentElement["value"] = "";
    }
  }

  private countDown(minutes: number, seconds: number): void {
    this.allowResend = false;

    const targetDate = new Date();
    targetDate.setMinutes(targetDate.getMinutes() + minutes);
    targetDate.setSeconds(targetDate.getSeconds() + seconds + 1);

    const subscription = timer(0, 1000).subscribe(() => {
      const currentDate = new Date(),
        remainingTime = targetDate.getTime() - currentDate.getTime();

      if (remainingTime <= 0) {
        subscription.unsubscribe(); // Stop the countdown when it reaches zero
        this.allowResend = true;
      } else {
        const minutes = Math.floor(remainingTime / (1000 * 60)),
          seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

        this.counter = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
    });
  }

  @HostListener("paste", ["$event"])
  pastValue(event: ClipboardEvent) {
    this.otpValue = event.clipboardData?.getData("Text") || "";

    setTimeout(() => {
      this.otpValue.split("").map((number, index) => {
        const currentElement = this.getCodeBoxElement(index + 1);
        if (currentElement) currentElement["value"] = number;
      });

      if (this.otpForm.get("otp")) this.otpForm.get("otp")?.setValue(this.otpValue);
    });
  }
}
